import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import PageTracker from "./components/PageTracker";
import ReactGA from "react-ga4";
import VLibras from "@djpfs/react-vlibras";
import { ToastContainer } from "react-toastify";
import UsePageTitle from "./hooks/UsePageTitle";

import Login from "./pages/Usuario/Login";
import DashboardComum from "./pages/Home/DashboardComum";
import DashboardPSF from "./pages/Home/DashboardPSF";
import ListarEspecialidades from "./pages/Servicos/ListarEspecialidades";
import GerenciarEspecialidades from "./pages/Servicos/GerenciarEspecialidades";
import DashboardSecretaria from "./pages/Home/DashboardSecretaria";
import CadastrarEspecialidade from "./pages/Servicos/CadastrarEspecialidade";
import PrivateRoute from "./routes/PrivateRoute";
import NovoUsuario from "./pages/Usuario/NovoUsuario";
import EditarPerfil from "./pages/Usuario/EditarPerfil";
import Acessibilidade from "./components/Acessibilidade";
import PainelEstatisticas from "./pages/Estatisticas/PainelEstatisticas";
import RecuperarSenha from "./pages/Usuario/RecuperarSenha";
import LogsAuditoria from "./pages/Auditoria/LogsAuditoria";
import EnviarDocumentos from "./pages/Documentos/EnviarDocumentos";
import AdicionarUBS from "./pages/UBS/AdicionarUBS";
import ListarUBS from "./pages/UBS/ListarUBS";
import TrocarSenha from "./pages/Usuario/TrocarSenha";
import TrocarSenhaUsuario from "./pages/Usuario/TrocarSenhaUsuario";



const RedirectByRole = () => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (role === "ROLE_SECRETARIA") {
    return <Navigate to="/solicitacoes/PENDENTE" replace />;
  } else if (role === "ROLE_COMUM") {
    return <Navigate to="/home" replace />;
  } else if (role === "ROLE_PSF") {
    return <Navigate to="/home_psf" replace />;
  } else {
    return <Navigate to="/login" replace />;
  }
};

const checkForUpdates = () => {
  const lastUpdate = localStorage.getItem("lastUpdate");
  const now = new Date();

  if (
    !lastUpdate ||
    new Date(lastUpdate).toDateString() !== now.toDateString()
  ) {
    localStorage.setItem("lastUpdate", now.toISOString());

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.update();

          if (registration.waiting) {
            // Se já existe um Service Worker esperando para ser ativado
            registration.waiting.postMessage({ type: "SKIP_WAITING" });
          }

          registration.addEventListener("updatefound", () => {
            const newWorker = registration.installing;

            if (newWorker) {
              newWorker.addEventListener("statechange", () => {
                if (newWorker.state === "installed") {
                  if (navigator.serviceWorker.controller) {
                    newWorker.postMessage({ type: "SKIP_WAITING" });
                  }
                }
              });
            }
          });
        });
      });
    }
  }
};

const App = () => {
  useEffect(() => {
    checkForUpdates();
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-FRQ1ZW0D0K";
    document.head.appendChild(script);

    const script2 = document.createElement("script");
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-FRQ1ZW0D0K');
    `;
    document.head.appendChild(script2);

    // 🔹 Inicializa o Google Analytics 4
    ReactGA.initialize("G-FRQ1ZW0D0K");
    ReactGA.send("pageview");
  }, []);

  return (
    <GoogleOAuthProvider clientId="52546167150-1bjvh2khaphr8t3asiv7k8mof80lclfg.apps.googleusercontent.com">
      <Router>
        <PageTracker />
        <UsePageTitle />
        <VLibras forceOnload={true} />
        <Acessibilidade />
        <Routes>
          
          <Route path="/" element={<RedirectByRole />} />
          {/* Autenticação e Acesso */}
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<RecuperarSenha />} />

          {/* Cadastro e Atualização de Usuários */}
          <Route path="/cadastrar-usuario" element={<NovoUsuario />} />
          <Route
            path="/atualizar-cadastro"
            element={
              <PrivateRoute>
                <EditarPerfil />
              </PrivateRoute>
            }
          />
          <Route
            path="/atualizar-senha"
            element={
              <PrivateRoute>
                <TrocarSenha />
              </PrivateRoute>
            }
          />
          <Route
            path="/atualizar-senha-usuario"
            element={
              <PrivateRoute allowedRoles={["ROLE_SECRETARIA"]}>
                <TrocarSenhaUsuario />
              </PrivateRoute>
            }
          />

          {/* Dashboards */}
          <Route
            path="/home"
            element={
              <PrivateRoute>
                <DashboardComum />
              </PrivateRoute>
            }
          />
          <Route
            path="/home_psf"
            allowedRoles={["ROLE_PSF"]}
            element={
              <PrivateRoute allowedRoles={["ROLE_PSF"]}>
                <DashboardPSF />
              </PrivateRoute>
            }
          />
          <Route
            path="/solicitacoes/:status"
            element={
              <PrivateRoute allowedRoles={["ROLE_SECRETARIA"]}>
                <DashboardSecretaria />
              </PrivateRoute>
            }
          />

          {/* Serviços e Especialidades */}
          <Route
            path="/servicos/cadastrar"
            element={
              <PrivateRoute allowedRoles={["ROLE_SECRETARIA"]}>
                <CadastrarEspecialidade />
              </PrivateRoute>
            }
          />
          <Route
            path="/servicos/listar/:categoria"
            element={
              <PrivateRoute>
                <ListarEspecialidades />
              </PrivateRoute>
            }
          />
          <Route
            path="/servicos/gerenciar/:categoria"
            element={
              <PrivateRoute allowedRoles={["ROLE_SECRETARIA"]}>
                <GerenciarEspecialidades />
              </PrivateRoute>
            }
          />

          {/* UBS */}

          <Route
            path="/ubs/cadastrar"
            element={
              <PrivateRoute allowedRoles={["ROLE_SECRETARIA"]}>
                <AdicionarUBS />
              </PrivateRoute>
            }
          />
          <Route
            path="/ubs/listar"
            element={
              <PrivateRoute allowedRoles={["ROLE_SECRETARIA"]}>
                <ListarUBS />
              </PrivateRoute>
            }
          />
          
          {/* Estatísticas e Auditoria */}
          <Route
            path="/estatisticas"
            element={
              <PrivateRoute allowedRoles={["ROLE_SECRETARIA"]}>
                <PainelEstatisticas />
              </PrivateRoute>
            }
          />
          <Route
            path="/auditoria"
            element={
              <PrivateRoute allowedRoles={["ROLE_SECRETARIA"]}>
                <LogsAuditoria />
              </PrivateRoute>
            }
          />

          {/* Documentos */}
          <Route
            path="/upload-documents/:token"
            element={<EnviarDocumentos />}
          />
          
          {/* Rota Padrão para Redirecionamento */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      <ToastContainer />
    </GoogleOAuthProvider>
  );
};

export default App;
